import { cn, type MentionUserMap } from "@/lib/utils";
import { MessageSquare } from "lucide-react";

interface MessageAuthor {
  id: number;
  username: string;
  firstName?: string | null;
  lastName?: string | null;
  colorCode?: string | null;
}

interface ChatAttachment {
  id: string;
  originalFilename: string;
  contentType: string;
  sizeBytes?: number;
  metadata?: {
    isImage?: boolean;
  };
}

interface ReactionEntry {
  id: string;
  targetType: string;
  targetId: string;
  reactorId: number;
  type: 'emoji' | 'text';
  content: string;
  createdAt: Date | string;
  reactor?: {
    id: number;
    username?: string;
    firstName?: string | null;
    lastName?: string | null;
    shortCode?: string | null;
    colorCode?: string | null;
  };
}

interface ThreadMessage {
  id: string;
  content: string;
  richContent?: any;
  authorId: number | null;
  integrationId?: string | null;
  integrationName?: string | null;
  integrationAvatar?: string | null;
  createdAt: Date | string;
  editedAt?: Date | null;
  author?: MessageAuthor | null;
  reactionEntries?: ReactionEntry[];
  attachments?: ChatAttachment[];
  metadata?: any;
  replyToId?: string | null;
}

interface MessageWithReplies extends ThreadMessage {
  replies: ThreadMessage[];
  isOrphanReply?: boolean;
}

interface PreviewAttachment {
  id: string;
  filename: string;
  contentType: string;
}

interface ThreadedMessageGroupProps {
  messageGroup: MessageWithReplies;
  currentUserId: number;
  channelId: string;
  highlightMessageId?: string | null;
  onHighlightComplete?: () => void;
  mentionUserMap?: MentionUserMap;
  onPreviewAttachment?: (attachment: PreviewAttachment) => void;
  onReply?: (messageId: string, authorName: string) => void;
  showRootAuthor?: boolean;
  channelType?: string;
  renderMessage: (props: {
    message: ThreadMessage;
    isOwn: boolean;
    showAuthor?: boolean;
    currentUserId?: number;
    channelId?: string;
    channelType?: string;
    compact?: boolean;
    highlightMessageId?: string | null;
    onHighlightComplete?: () => void;
    mentionUserMap?: MentionUserMap;
    onPreviewAttachment?: (attachment: PreviewAttachment) => void;
    isReply?: boolean;
    onReply?: (messageId: string, authorName: string) => void;
  }) => React.ReactNode;
}

export function ThreadedMessageGroup({
  messageGroup,
  currentUserId,
  channelId,
  channelType,
  highlightMessageId,
  onHighlightComplete,
  mentionUserMap,
  onPreviewAttachment,
  onReply,
  showRootAuthor = true,
  renderMessage,
}: ThreadedMessageGroupProps) {
  const { replies, isOrphanReply } = messageGroup;
  const isOwn = messageGroup.authorId === currentUserId;
  const hasReplies = replies.length > 0;

  return (
    <div className={cn("relative", showRootAuthor ? "" : "-mt-2")} data-message-id={messageGroup.id}>
      {isOrphanReply && (
        <div className={cn(
          "flex items-center gap-1.5 text-[10px] text-muted-foreground mb-1",
          isOwn ? "justify-end mr-2" : "justify-start ml-2"
        )}>
          <MessageSquare className="w-3 h-3" />
          <span>Replying to earlier thread ⤴</span>
        </div>
      )}
      
      {renderMessage({
        message: messageGroup,
        isOwn,
        showAuthor: showRootAuthor,
        currentUserId,
        channelId,
        channelType,
        compact: false,
        highlightMessageId,
        onHighlightComplete,
        mentionUserMap,
        onPreviewAttachment,
        isReply: isOrphanReply,
        onReply,
      })}

      {hasReplies && (
        <div className={cn(
          "relative mt-2",
          isOwn ? "flex flex-col items-end" : "flex flex-col items-start"
        )}>
          <div 
            className={cn(
              "absolute w-5 border-muted-foreground/30",
              isOwn 
                ? "border-r-2 border-b-2 rounded-br-xl" 
                : "border-l-2 border-b-2 rounded-bl-xl"
            )}
            style={{ 
              top: '-0.5rem',
              bottom: '1rem',
              [isOwn ? 'right' : 'left']: '2.5rem',
            }}
          />
          
          <div className={cn(
            "space-y-1",
            isOwn ? "pr-14" : "pl-14"
          )}>
            {replies.map((reply) => {
              const replyIsOwn = reply.authorId === currentUserId;
              
              return (
                <div 
                  key={reply.id} 
                  className={cn(
                    "relative",
                    isOwn ? "flex justify-end" : "flex justify-start"
                  )}
                  data-message-id={reply.id}
                >
                  {renderMessage({
                    message: reply,
                    isOwn: replyIsOwn,
                    showAuthor: true,
                    currentUserId,
                    channelId,
                    channelType,
                    compact: true,
                    highlightMessageId,
                    onHighlightComplete,
                    mentionUserMap,
                    onPreviewAttachment,
                    isReply: true,
                    onReply,
                  })}
                </div>
              );
            })}
          </div>
          
          <div className={cn(
            "flex items-center gap-1 text-[10px] text-muted-foreground mt-1",
            isOwn ? "pr-14" : "pl-14"
          )}>
            <MessageSquare className="w-3 h-3" />
            <span>{replies.length} {replies.length === 1 ? 'reply' : 'replies'}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export function OrphanReplyIndicator() {
  return (
    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mb-1 ml-2">
      <MessageSquare className="w-3 h-3" />
      <span>Replying to earlier thread ⤴</span>
    </div>
  );
}
